import React, { useState, useEffect } from 'react';
import api from '../services/api';

const ReportPage = () => {
    const [reportType, setReportType] = useState('daily');
    const [aggregationLevel, setAggregationLevel] = useState('none');
    const [reportPeriod, setReportPeriod] = useState('monthly');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const today = new Date();
        return today.toISOString().slice(0, 7); // "YYYY-MM"
    });
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const [userIdToUsername, setUserIdToUsername] = useState({});
    const [deviceIdToDeviceInfo, setDeviceIdToDeviceInfo] = useState({});

    useEffect(() => {
        setReportData(null);
        setError(null);
        setSuccessMessage(null);
    }, [reportType, aggregationLevel, reportPeriod]);

    useEffect(() => {
        const fetchUsersAndDevices = async () => {
            try {
                const usersResponse = await api.get('/users/');
                const users = usersResponse.data;
                const userMap = {};
                users.forEach((user) => {
                    userMap[user.id] = user.username;
                });
                setUserIdToUsername(userMap);

                const devicesResponse = await api.get('/devices/');
                const devices = devicesResponse.data;
                const deviceInfoMap = {};
                devices.forEach((device) => {
                    deviceInfoMap[device.id] = device;
                });
                setDeviceIdToDeviceInfo(deviceInfoMap);
            } catch (error) {
                console.error('Failed to fetch users or devices', error);
                setError('Nie udało się pobrać listy użytkowników lub urządzeń.');
            }
        };

        fetchUsersAndDevices();
    }, []);

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getAggregationName = (item) => {
        if (aggregationLevel === 'by_owner') {
            return userIdToUsername[item.aggregation_id] || '...';
        } else if (aggregationLevel === 'none') {
            const device = deviceIdToDeviceInfo[item.aggregation_id];
            return device ? device.name : 'Nieznane PPE';
        } else {
            return '';
        }
    };

    const getUsernameForAggregationItem = (item) => {
        if (aggregationLevel === 'none') {
            const device = deviceIdToDeviceInfo[item.aggregation_id];
            if (device) {
                const username = userIdToUsername[device.user_id];
                return username || 'Nieznany użytkownik';
            }
            return 'Nieznane urządzenie';
        }
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setReportData(null);
        setError(null);
        setSuccessMessage(null);

        if (reportPeriod === 'custom') {
            if (!startDate || !endDate) {
                setError('Proszę podać datę początkową i końcową.');
                setLoading(false);
                return;
            }
            if (new Date(startDate) > new Date(endDate)) {
                setError('Data początkowa nie może być późniejsza niż data końcowa.');
                setLoading(false);
                return;
            }
        } else if (reportPeriod === 'monthly') {
            if (!selectedMonth) {
                setError('Proszę wybrać miesiąc.');
                setLoading(false);
                return;
            }
        }

        try {
            let params = {};
            if (reportPeriod === 'monthly') {
                const [year, month] = selectedMonth.split('-').map(Number);
                const firstDay = new Date(year, month - 1, 1);
                const lastDay = new Date(year, month, 0);

                params = {
                    start_date: formatDate(firstDay),
                    end_date: formatDate(lastDay),
                };
            } else if (reportPeriod === 'custom') {
                params = {
                    start_date: formatDate(new Date(startDate)),
                    end_date: formatDate(new Date(endDate)),
                };
            }

            let endpoint = '';
            if (reportType === 'daily') {
                endpoint = '/reports/report_daily';
            } else if (reportType === 'hourly') {
                endpoint = '/reports/report_hourly';
            } else if (reportType === 'total') {
                endpoint = '/reports/report_total';
            }

            const response = await api.get(endpoint, {
                params: {
                    ...params,
                    aggregation_level: aggregationLevel,
                },
            });

            setReportData(response.data || []);
            setSuccessMessage('Raport został pomyślnie wygenerowany.');
        } catch (error) {
            setError(
                error.response?.data?.detail || 'Błąd podczas pobierania danych raportu'
            );
            console.error('Error fetching report data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            let params = {};
            if (reportPeriod === 'monthly') {
                const [year, month] = selectedMonth.split('-').map(Number);
                const firstDay = new Date(year, month - 1, 1);
                const lastDay = new Date(year, month, 0);

                params = {
                    start_date: formatDate(firstDay),
                    end_date: formatDate(lastDay),
                    output_format: 'csv',
                };
            } else if (reportPeriod === 'custom') {
                params = {
                    start_date: formatDate(new Date(startDate)),
                    end_date: formatDate(new Date(endDate)),
                    output_format: 'csv',
                };
            }

            let endpoint = '';
            if (reportType === 'daily') {
                endpoint = '/reports/report_daily';
            } else if (reportType === 'hourly') {
                endpoint = '/reports/report_hourly';
            } else if (reportType === 'total') {
                endpoint = '/reports/report_total';
            }

            const response = await api.get(endpoint, {
                params: {
                    ...params,
                    aggregation_level: aggregationLevel,
                },
                responseType: 'blob',
            });

            // Tworzenie URL dla pliku
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            // Opcjonalnie: Nazwa pliku może być dynamiczna
            link.setAttribute('download', `raport_${reportType}_${formatDate(new Date())}.csv`);
            document.body.appendChild(link);
            link.click();

            // Czyszczenie
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            setSuccessMessage('Raport został pomyślnie pobrany.');
        } catch (error) {
            setError(
                error.response?.data?.detail || 'Błąd podczas pobierania raportu'
            );
            console.error('Error downloading report:', error);
        } finally {
            setDownloading(false);
        }
    };

    // Funkcje renderujące raporty
    const renderHourlyReport = () => {
        if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
            return <p>Brak danych do wyświetlenia.</p>;
        }

        const showAggregationColumn = aggregationLevel !== 'all';
        const showUserNameColumn = aggregationLevel === 'none';
        const showStatusColumn = aggregationLevel === 'none';

        return (
            <table className="min-w-full bg-white border border-gray-200">
                <thead>
                <tr>
                    <th className="border px-4 py-2">Znacznik czasu</th>
                    {showAggregationColumn && (
                        <th className="border px-4 py-2">
                            {aggregationLevel === 'by_owner' ? 'Nazwa użytkownika' : 'Nazwa PPE'}
                        </th>
                    )}
                    {showUserNameColumn && (
                        <th className="border px-4 py-2">Nazwa użytkownika</th>
                    )}
                    <th className="border px-4 py-2">Całkowita produkcja</th>
                    <th className="border px-4 py-2">Całkowite zużycie</th>
                    <th className="border px-4 py-2">Energia z nadwyżki</th>
                    <th className="border px-4 py-2">Energia do magazynu</th>
                    <th className="border px-4 py-2">Energia z magazynu użytkownika</th>
                    <th className="border px-4 py-2">Energia z magazynu ogólnego</th>
                    <th className="border px-4 py-2">Energia z zewnętrznych źródeł</th>
                    <th className="border px-4 py-2">Bilans rozlicznia</th>
                    {showStatusColumn && (
                        <th className="border px-4 py-2">Status PPE</th>
                    )}
                </tr>
                </thead>
                <tbody>
                {reportData.map((report, index) =>
                    report.reports.map((item, reportIndex) => (
                        <tr key={`${index}-${reportIndex}`}>
                            <td className="border px-4 py-2">
                                {new Date(report.timestamp).toLocaleString()}
                            </td>
                            {showAggregationColumn && (
                                <td className="border px-4 py-2">{getAggregationName(item)}</td>
                            )}
                            {showUserNameColumn && (
                                <td className="border px-4 py-2">
                                    {getUsernameForAggregationItem(item)}
                                </td>
                            )}
                            <td className="border px-4 py-2">{item.total_production}</td>
                            <td className="border px-4 py-2">{item.total_consumption}</td>
                            <td className="border px-4 py-2">{item.energy_from_surplus}</td>
                            <td className="border px-4 py-2">{item.energy_to_storage}</td>
                            <td className="border px-4 py-2">{item.energy_from_user_storage}</td>
                            <td className="border px-4 py-2">{item.energy_from_general_storage}</td>
                            <td className="border px-4 py-2">{item.energy_from_external}</td>
                            <td className="border px-4 py-2">{item.energy_balance}</td>
                            {showStatusColumn && (
                                <td className="border px-4 py-2">
                                    <span className={`font-semibold ${
                                        item.status === 'Active' ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        {item.status === 'Active' ? 'Aktywny' : 'Nieaktywny'}
                                    </span>
                                </td>
                            )}
                        </tr>
                    ))
                )}
                </tbody>
            </table>
        );
    };

    const renderDailyReport = () => {
        if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
            return <p>Brak danych do wyświetlenia.</p>;
        }

        const showAggregationColumn = aggregationLevel !== 'all';
        const showUserNameColumn = aggregationLevel === 'none';
        const showStatusColumn = aggregationLevel === 'none';

        return (
            <table className="min-w-full bg-white border border-gray-200">
                <thead>
                <tr>
                    <th className="border px-4 py-2">Data</th>
                    {showAggregationColumn && (
                        <th className="border px-4 py-2">
                            {aggregationLevel === 'by_owner' ? 'Nazwa użytkownika' : 'Nazwa PPE'}
                        </th>
                    )}
                    {showUserNameColumn && (
                        <th className="border px-4 py-2">Nazwa użytkownika</th>
                    )}
                    <th className="border px-4 py-2">Całkowita produkcja</th>
                    <th className="border px-4 py-2">Całkowite zużycie</th>
                    <th className="border px-4 py-2">Energia z nadwyżki</th>
                    <th className="border px-4 py-2">Energia do magazynu</th>
                    <th className="border px-4 py-2">Energia z magazynu użytkownika</th>
                    <th className="border px-4 py-2">Energia z magazynu ogólnego</th>
                    <th className="border px-4 py-2">Energia z zewnętrznych źródeł</th>
                    <th className="border px-4 py-2">Bilans rozlicznia</th>
                    {showStatusColumn && (
                        <th className="border px-4 py-2">Status PPE</th>
                    )}
                </tr>
                </thead>
                <tbody>
                {reportData.map((report, index) =>
                    report.reports.map((item, reportIndex) => (
                        <tr key={`${index}-${reportIndex}`}>
                            <td className="border px-4 py-2">{report.date}</td>
                            {showAggregationColumn && (
                                <td className="border px-4 py-2">{getAggregationName(item)}</td>
                            )}
                            {showUserNameColumn && (
                                <td className="border px-4 py-2">
                                    {getUsernameForAggregationItem(item)}
                                </td>
                            )}
                            <td className="border px-4 py-2">{item.total_production}</td>
                            <td className="border px-4 py-2">{item.total_consumption}</td>
                            <td className="border px-4 py-2">{item.energy_from_surplus}</td>
                            <td className="border px-4 py-2">{item.energy_to_storage}</td>
                            <td className="border px-4 py-2">{item.energy_from_user_storage}</td>
                            <td className="border px-4 py-2">{item.energy_from_general_storage}</td>
                            <td className="border px-4 py-2">{item.energy_from_external}</td>
                            <td className="border px-4 py-2">{item.energy_balance}</td>
                            {showStatusColumn && (
                                <td className="border px-4 py-2">
                                    <span className={`font-semibold ${
                                        item.status === 'Active' ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        {item.status === 'Active' ? 'Aktywny' : 'Nieaktywny'}
                                    </span>
                                </td>
                            )}
                        </tr>
                    ))
                )}
                </tbody>
            </table>
        );
    };

    const renderTotalReport = () => {
        if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
            return <p>Brak danych do wyświetlenia.</p>;
        }

        const showAggregationColumn = aggregationLevel !== 'all';
        const showUserNameColumn = aggregationLevel === 'none';
        const showStatusColumn = aggregationLevel === 'none';

        return (
            <table className="min-w-full bg-white border border-gray-200">
                <thead>
                <tr>
                    {showAggregationColumn && (
                        <th className="border px-4 py-2">
                            {aggregationLevel === 'by_owner' ? 'Nazwa użytkownika' : 'Nazwa PPE'}
                        </th>
                    )}
                    {showUserNameColumn && (
                        <th className="border px-4 py-2">Nazwa użytkownika</th>
                    )}
                    <th className="border px-4 py-2">Całkowita produkcja</th>
                    <th className="border px-4 py-2">Całkowite zużycie</th>
                    <th className="border px-4 py-2">Energia z nadwyżki</th>
                    <th className="border px-4 py-2">Energia do magazynu</th>
                    <th className="border px-4 py-2">Energia z magazynu użytkownika</th>
                    <th className="border px-4 py-2">Energia z magazynu ogólnego</th>
                    <th className="border px-4 py-2">Energia z zewnętrznych źródeł</th>
                    <th className="border px-4 py-2">Bilans rozlicznia</th>
                    {showStatusColumn && (
                        <th className="border px-4 py-2">Status PPE</th>
                    )}
                </tr>
                </thead>
                <tbody>
                {reportData.flatMap((report, index) =>
                    report.reports.map((item, reportIndex) => (
                        <tr key={`${index}-${reportIndex}`}>
                            {showAggregationColumn && (
                                <td className="border px-4 py-2">{getAggregationName(item)}</td>
                            )}
                            {showUserNameColumn && (
                                <td className="border px-4 py-2">
                                    {getUsernameForAggregationItem(item)}
                                </td>
                            )}
                            <td className="border px-4 py-2">{item.total_production}</td>
                            <td className="border px-4 py-2">{item.total_consumption}</td>
                            <td className="border px-4 py-2">{item.energy_from_surplus}</td>
                            <td className="border px-4 py-2">{item.energy_to_storage}</td>
                            <td className="border px-4 py-2">{item.energy_from_user_storage}</td>
                            <td className="border px-4 py-2">{item.energy_from_general_storage}</td>
                            <td className="border px-4 py-2">{item.energy_from_external}</td>
                            <td className="border px-4 py-2">{item.energy_balance}</td>
                            {showStatusColumn && (
                                <td className="border px-4 py-2">
                                    <span className={`font-semibold ${
                                        item.status === 'Active' ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        {item.status === 'Active' ? 'Aktywny' : 'Nieaktywny'}
                                    </span>
                                </td>
                            )}
                        </tr>
                    ))
                )}
                </tbody>
            </table>
        );
    };

    return (
        <div className="min-h-screen flex flex-col items-center p-6">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-full w-full">
                <h2 className="text-2xl font-bold mb-6">Generowanie raportów</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">
                            Wybierz typ raportu:
                        </label>
                        <select
                            value={reportType}
                            onChange={(e) => setReportType(e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg"
                        >
                            <option value="daily">Raport dzienny</option>
                            <option value="hourly">Raport godzinowy</option>
                            <option value="total">Raport sumaryczny</option>
                        </select>
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">
                            Poziom agregacji:
                        </label>
                        <select
                            value={aggregationLevel}
                            onChange={(e) => setAggregationLevel(e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg"
                        >
                            <option value="none">Brak</option>
                            <option value="by_owner">Według użytkownika</option>
                            <option value="all">Wszystkie</option>
                        </select>
                    </div>

                    <div className="mb-6">
                        <label className="block text-gray-700 mb-2">
                            Wybierz okres raportu:
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    value="monthly"
                                    checked={reportPeriod === 'monthly'}
                                    onChange={(e) => setReportPeriod(e.target.value)}
                                    className="mr-2"
                                />
                                Raport miesięczny
                            </label>
                            <span className="text-gray-500">lub</span>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    value="custom"
                                    checked={reportPeriod === 'custom'}
                                    onChange={(e) => setReportPeriod(e.target.value)}
                                    className="mr-2"
                                />
                                Raport na dowolny zakres dat
                            </label>
                        </div>
                    </div>

                    <div className="mb-4 flex flex-wrap space-x-4">
                        {reportPeriod === 'monthly' ? (
                            <div>
                                <label htmlFor="month" className="block text-gray-700 mb-2">
                                    Miesiąc:
                                </label>
                                <input
                                    type="month"
                                    id="month"
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    className="w-64 p-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                        ) : (
                            <>
                                <div>
                                    <label htmlFor="start-date" className="block text-gray-700 mb-2">
                                        Data początkowa:
                                    </label>
                                    <input
                                        type="date"
                                        id="start-date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-64 p-2 border border-gray-300 rounded-lg"
                                        required={reportPeriod === 'custom'}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="end-date" className="block text-gray-700 mb-2">
                                        Data końcowa:
                                    </label>
                                    <input
                                        type="date"
                                        id="end-date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-64 p-2 border border-gray-300 rounded-lg"
                                        required={reportPeriod === 'custom'}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex space-x-4">
                        <button
                            type="submit"
                            className="bg-softTeal text-pureWhite font-semibold px-4 py-2 rounded-lg hover:bg-warmOrange transition"
                            disabled={loading || downloading}
                        >
                            {loading ? 'Generowanie raportu...' : 'Generuj raport'}
                        </button>
                        <button
                            type="button"
                            onClick={handleDownload}
                            className="bg-transparent text-softTeal font-semibold px-4 py-2 border-2 border-softTeal rounded-lg hover:bg-softTeal hover:text-pureWhite transition"
                            disabled={loading || downloading}
                        >
                            {downloading ? 'Pobieranie...' : 'Pobierz CSV'}
                        </button>
                    </div>
                </form>

                {error && <p className="text-red-500 mt-4">{error}</p>}
                {successMessage && <p className="text-green-500 mt-4">{successMessage}</p>}

                {reportData && !loading && !error && (
                    <div className="mt-8">
                        <h3 className="text-xl font-bold mb-4">Wyniki raportu</h3>
                        <div className="overflow-x-auto">
                            {reportType === 'daily' && renderDailyReport()}
                            {reportType === 'hourly' && renderHourlyReport()}
                            {reportType === 'total' && renderTotalReport()}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportPage;
